import { keepPreviousData, useQuery } from '@tanstack/react-query'
import type { PaginationState, SortingState } from '@tanstack/react-table'

import { type AdminPaginatedSubstantiations } from 'utils/web_api.schemas'

import { type GrapeAPIErrorType } from '../GrapeAPIError'

export type SubstantiationsSingleValueFilters = {
  after_date?: string
  before_date?: string
  vip_employer?: string
  has_receipts?: string
  payment_method_unit_card?: string
}

export type SubstantiationsArrayValueFilters = {
  review_statuses?: string[]
  medical_care?: string[]
  ongoing_coverage?: string[]
  member_ids?: string[]
  employer_ids?: string[]
  reviewed_by_ids?: string[]
  broker_ids?: string[]
}

export type SubstantiationsFilters = SubstantiationsSingleValueFilters & SubstantiationsArrayValueFilters

export interface GetSubstantiationsParams {
  filters: SubstantiationsFilters
  pagination: PaginationState
  sorting: SortingState
}

const fetchSubstantiations = async ({
  filters,
  pagination,
  sorting,
}: GetSubstantiationsParams): Promise<AdminPaginatedSubstantiations> => {
  const params = new URLSearchParams()

  // Pagination
  params.append('page', pagination.pageIndex.toString())
  params.append('items', pagination.pageSize.toString())

  // Sorting
  if (sorting.length > 0) {
    const sortKey = sorting[0]?.id
    const sortOrder = sorting[0]?.desc ? 'desc' : 'asc'
    if (sortKey && ['review_status', 'created_at', 'reviewed_at'].includes(sortKey)) {
      params.append(`sort[${sortKey}]`, sortOrder)
    }
  }

  // Filters
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        value.forEach((v) => {
          if (v !== '') {
            params.append(`filter[${key}][]`, v)
          }
        })
      } else {
        params.append(`filter[${key}]`, value.toString())
      }
    }
  })

  const response = await fetch(`/api/web/admin/substantiations?${params}`, { // eslint-disable-line thatch/no-bare-fetch
    headers: {
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch substantiations: ${response.statusText}`)
  }

  return response.json()
}

export const getSubstantiationsQueryKey = ({
  filters,
  pagination,
  sorting,
}: GetSubstantiationsParams) => [
  'substantiations',
  filters,
  pagination,
  sorting,
]

export const useGetSubstantiations = ({
  filters,
  pagination,
  sorting,
}: GetSubstantiationsParams) => {
  return useQuery<AdminPaginatedSubstantiations, GrapeAPIErrorType>({
    queryKey: getSubstantiationsQueryKey({ filters, pagination, sorting }),
    queryFn: () => fetchSubstantiations({ filters, pagination, sorting }),
    placeholderData: keepPreviousData,
  })
}
